import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Eye, EyeOff, Clock, Rss, BarChart3, Plus, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminChartsTab() {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const fetchConfig = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/charts/config/admin`, { withCredentials: true }); setConfig(data); }
    catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const save = async () => {
    setSaving(true); setSaved(false);
    try { await axios.put(`${API}/charts/config`, config, { withCredentials: true }); setSaved(true); setTimeout(() => setSaved(false), 2000); }
    catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const updateChart = (idx, field, value) => {
    const charts = [...config.charts];
    charts[idx] = { ...charts[idx], [field]: value };
    setConfig({ ...config, charts });
  };

  const addChart = () => {
    const id = `chart_${Date.now()}`;
    setConfig({ ...config, charts: [...config.charts, { id, label: "Neuer Chart", heading: "", embed_code: "", visible: true, time_from: "", time_to: "" }] });
  };

  const removeChart = (idx) => {
    setConfig({ ...config, charts: config.charts.filter((_, i) => i !== idx) });
  };

  const updateRss = (field, value) => setConfig({ ...config, rss: { ...config.rss, [field]: value } });

  if (!config) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-[#C5993A] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-heading text-xl text-white">Charts & Marktdaten</h3>
          <p className="text-xs text-[#3A4F60] mt-1">Embed-Code, Überschrift, Sichtbarkeit und Zeitsteuerung</p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-gradient-to-r from-[#C5993A] to-[#8B6B20] text-white border-0 text-sm">
          {saving ? "Speichern..." : saved ? "Gespeichert!" : "Speichern"}
        </Button>
      </div>

      {/* Charts */}
      <div className="space-y-4 mb-8">
        <div className="flex items-center justify-between">
          <h4 className="text-sm text-[#C5993A] font-medium flex items-center gap-2"><BarChart3 className="w-4 h-4" /> Charts</h4>
          <Button onClick={addChart} variant="ghost" size="sm" className="text-[#C5993A] text-xs"><Plus className="w-3 h-3 mr-1" /> Neuer Chart</Button>
        </div>

        {config.charts.map((ch, i) => (
          <div key={ch.id} className="bg-[#12202f] border border-[#3A4F60]/30 rounded-lg p-5">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <span className="text-white text-sm font-medium">{ch.label || `Chart ${i + 1}`}</span>
                <Button onClick={() => updateChart(i, "visible", !ch.visible)} variant="ghost" size="sm" className={ch.visible ? "text-green-400 h-7" : "text-red-400 h-7"}>
                  {ch.visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                </Button>
              </div>
              <Button onClick={() => removeChart(i)} variant="ghost" size="sm" className="text-[#8899AA] hover:text-red-400 h-7"><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>

            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] text-[#8899AA] mb-1">Bezeichnung (intern)</label>
                  <Input value={ch.label || ""} onChange={(e) => updateChart(i, "label", e.target.value)} className="bg-[#0C1620] border-[#3A4F60]/50 text-white text-xs h-8" />
                </div>
                <div>
                  <label className="block text-[10px] text-[#8899AA] mb-1">Überschrift (auf Website, leer = keine)</label>
                  <Input value={ch.heading || ""} onChange={(e) => updateChart(i, "heading", e.target.value)} className="bg-[#0C1620] border-[#3A4F60]/50 text-white text-xs h-8" placeholder="z.B. Goldpreisentwicklung" />
                </div>
              </div>
              <div>
                <label className="block text-[10px] text-[#8899AA] mb-1">HTML Embed-Code</label>
                <Textarea value={ch.embed_code || ""} onChange={(e) => updateChart(i, "embed_code", e.target.value)} rows={3} className="bg-[#0C1620] border-[#3A4F60]/50 text-white text-xs resize-none font-mono" placeholder='<a href="..."><img src="..." alt="..."></a>' />
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-3 h-3 text-[#8899AA]" />
                <span className="text-[10px] text-[#8899AA]">Zeitfenster:</span>
                <Input type="time" value={ch.time_from || ""} onChange={(e) => updateChart(i, "time_from", e.target.value)} className="bg-[#0C1620] border-[#3A4F60]/50 text-white text-xs w-24 h-7" />
                <span className="text-[#3A4F60] text-xs">-</span>
                <Input type="time" value={ch.time_to || ""} onChange={(e) => updateChart(i, "time_to", e.target.value)} className="bg-[#0C1620] border-[#3A4F60]/50 text-white text-xs w-24 h-7" />
                <span className="text-[10px] text-[#3A4F60]">{(!ch.time_from || !ch.time_to) ? "(ganztägig)" : "Uhr"}</span>
              </div>
            </div>

            {/* Preview */}
            {ch.embed_code && (
              <div className="mt-3 pt-3 border-t border-[#3A4F60]/20">
                <p className="text-[10px] text-[#3A4F60] mb-1">Vorschau:</p>
                <div className="bg-[#0C1620] rounded p-2 max-h-32 overflow-hidden [&_img]:max-h-28 [&_img]:w-auto [&_img]:object-contain">
                  <div dangerouslySetInnerHTML={{ __html: ch.embed_code }} />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* RSS Feed */}
      <div>
        <h4 className="text-sm text-[#C5993A] font-medium flex items-center gap-2 mb-3"><Rss className="w-4 h-4" /> RSS-Feed (gold.de)</h4>
        <div className="bg-[#12202f] border border-[#3A4F60]/30 rounded-lg p-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-1">
              <p className="text-white text-sm font-medium">Gold.de Nachrichten</p>
              <p className="text-[#3A4F60] text-xs">Zeigt die 5 neuesten Meldungen von gold.de</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Clock className="w-3 h-3 text-[#8899AA]" />
              <Input type="time" value={config.rss?.time_from || ""} onChange={(e) => updateRss("time_from", e.target.value)} className="bg-[#0C1620] border-[#3A4F60]/50 text-white text-xs w-24 h-8" />
              <span className="text-[#3A4F60] text-xs">-</span>
              <Input type="time" value={config.rss?.time_to || ""} onChange={(e) => updateRss("time_to", e.target.value)} className="bg-[#0C1620] border-[#3A4F60]/50 text-white text-xs w-24 h-8" />
            </div>
            <Button onClick={() => updateRss("visible", !config.rss?.visible)} variant="ghost" size="sm" className={config.rss?.visible ? "text-green-400" : "text-red-400"}>
              {config.rss?.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
